import { useMemo, useState } from 'react';
import type { Review, ReviewStatus, Staff } from '@salonomy/types';
import { Button, Card, EmptyState, Pill, SegmentedControl, Spinner } from '@salonomy/ui';
import { useReviews } from '../../data/reviews.js';
import { useStaff } from '../../data/salon.js';
import { ReviewModerationModal } from './ReviewModerationModal.js';
import './reviews.css';

type StatusFilter = '' | ReviewStatus;

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'pending', label: 'Pending' },
  { value: 'hidden', label: 'Hidden' },
];

/** ★★★★☆ — the rating as five glyphs (display only; the value is the server's). */
function Stars({ rating }: { rating: number }) {
  return (
    <span className="reviews-card__stars" aria-label={`${rating} out of 5`}>
      {'★'.repeat(rating)}
      {'☆'.repeat(5 - rating)}
    </span>
  );
}

/** A short, locale-aware date (display only). Falls back to a dash when the timestamp is absent. */
function shortDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const STATUS_TONE: Record<ReviewStatus, 'success' | 'neutral' | 'danger'> = {
  published: 'success',
  pending: 'neutral',
  hidden: 'danger',
};

/**
 * The operator moderation list + the salon's aggregate header + a per-stylist breakdown. The aggregate
 * (averageRating / reviewCount) is the SERVER's published figure — we display it, never recompute it.
 * The per-stylist summary IS derived in JS from the loaded rows purely to label the breakdown table; it
 * is an operator convenience over the current page, not a public figure (the public rating stays the
 * server's). Reading the list is allowed for any staff role; the publish / hide / reply actions are
 * manager-only and are rendered only when `canModerate` is true (the page passes the auth gate down).
 */
export function ReviewsPanel({ canModerate }: { canModerate: boolean }) {
  const [status, setStatus] = useState<StatusFilter>('');
  const [modal, setModal] = useState<{ review: Review; mode: 'reply' | 'hide' | 'publish' } | null>(
    null,
  );

  const { data, isLoading, isError } = useReviews(status || null);
  const { data: staff } = useStaff();

  const rows = data?.data ?? [];

  // A staffId → displayName map so a row and the breakdown can label the stylist without a join.
  const staffName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of (staff ?? []) as Staff[]) m.set(s.id, s.displayName);
    return m;
  }, [staff]);

  // Per-stylist breakdown over the LOADED rows (count + simple mean). This is an operator convenience —
  // the public, authoritative aggregate is the server's header figure, not this page-local mean.
  const byStylist = useMemo(() => {
    const acc = new Map<string, { name: string; count: number; sum: number }>();
    for (const r of rows) {
      const key = r.staffId ?? 'unattributed';
      const name = r.staffId ? (staffName.get(r.staffId) ?? 'Unknown stylist') : 'Whole salon';
      const cur = acc.get(key) ?? { name, count: 0, sum: 0 };
      cur.count += 1;
      cur.sum += r.rating;
      acc.set(key, cur);
    }
    return Array.from(acc.values())
      .map((x) => ({ name: x.name, count: x.count, avg: x.sum / x.count }))
      .sort((a, b) => b.count - a.count);
  }, [rows, staffName]);

  return (
    <>
      <Card>
        <div className="reviews-head">
          <div>
            <div className="section-title" style={{ marginBottom: 2 }}>
              Reviews
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
              Verified-visit reviews. Published ones show on your public booking page and count
              towards your star rating.
            </div>
          </div>
          {data ? (
            <div className="reviews-aggregate" title="Your public star rating (published reviews)">
              <span className="reviews-aggregate__score">{data.averageRating.toFixed(1)}</span>
              <span className="reviews-aggregate__stars">
                {'★'.repeat(Math.round(data.averageRating))}
                {'☆'.repeat(5 - Math.round(data.averageRating))}
              </span>
              <span className="muted" style={{ fontSize: 13 }}>
                {data.reviewCount} published
              </span>
            </div>
          ) : null}
        </div>

        <div className="reviews-filters">
          <SegmentedControl options={STATUS_FILTERS} value={status} onChange={setStatus} />
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
            <Spinner size={16} />
            <span className="muted" style={{ fontSize: 13 }}>
              Loading reviews…
            </span>
          </div>
        ) : isError ? (
          <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
            Couldn&rsquo;t load reviews. Please try again.
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No reviews here yet"
            description="Reviews arrive after a client completes a visit and is sent a request. Ask a completed appointment for a review from the diary, or turn on auto-request in Settings."
          />
        ) : (
          <div className="reviews-list">
            {rows.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                staffName={r.staffId ? staffName.get(r.staffId) : undefined}
                canModerate={canModerate}
                onReply={() => setModal({ review: r, mode: 'reply' })}
                onHide={() => setModal({ review: r, mode: 'hide' })}
                onPublish={() => setModal({ review: r, mode: 'publish' })}
              />
            ))}
          </div>
        )}
      </Card>

      {byStylist.length > 0 ? (
        <Card>
          <div className="section-title" style={{ marginBottom: 2 }}>
            By stylist
          </div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Over the reviews shown above. Your public rating is the salon-wide figure at the top.
          </div>
          <div className="reviews-bystylist">
            <div className="reviews-bystylist__row reviews-bystylist__row--head">
              <span>Stylist</span>
              <span className="reviews-num">Reviews</span>
              <span className="reviews-num">Average</span>
            </div>
            {byStylist.map((s) => (
              <div key={s.name} className="reviews-bystylist__row">
                <span>{s.name}</span>
                <span className="reviews-num mono">{s.count}</span>
                <span className="reviews-num mono">{s.avg.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {modal ? (
        <ReviewModerationModal
          review={modal.review}
          mode={modal.mode}
          onClose={() => setModal(null)}
        />
      ) : null}
    </>
  );
}

function ReviewRow({
  review,
  staffName,
  canModerate,
  onReply,
  onHide,
  onPublish,
}: {
  review: Review;
  staffName: string | undefined;
  canModerate: boolean;
  onReply: () => void;
  onHide: () => void;
  onPublish: () => void;
}) {
  return (
    <div className="reviews-card">
      <div className="reviews-card__top">
        <div className="reviews-card__who">
          <span className="reviews-card__author">{review.authorDisplayName}</span>
          <Stars rating={review.rating} />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Pill tone={STATUS_TONE[review.status]} className="mono">
            {review.status}
          </Pill>
        </div>
      </div>

      <div className="muted reviews-card__meta">
        {staffName ? `${staffName} · ` : ''}
        {shortDate(review.submittedAt ?? review.createdAt)}
      </div>

      {review.body ? <div className="reviews-card__body">{review.body}</div> : null}

      {review.salonReply ? (
        <div className="reviews-reply">
          <div className="reviews-reply__label">Salon reply</div>
          <div className="reviews-reply__body">{review.salonReply}</div>
        </div>
      ) : null}

      {canModerate ? (
        <div className="reviews-card__actions">
          {review.status !== 'published' ? (
            <Button size="sm" onClick={onPublish}>
              Publish
            </Button>
          ) : null}
          {review.status !== 'hidden' ? (
            <Button size="sm" variant="outline" onClick={onHide}>
              Hide
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onReply}>
            {review.salonReply ? 'Edit reply' : 'Reply'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
